import React from "react";
import {
  Building2,
  FileCheck2,
  MapPinned,
  Ruler,
  Handshake,
  ShieldCheck,
} from "lucide-react";

import bulding from "../../../assets/blogs/greenland.jpg";

const GreenlandAbout = () => {
  const brandPurple = "#bd06b3";
  const brandBlue = "#00bfff";

  const services = [
    {
      icon: Building2,
      title: "Residential Properties",
      description:
        "Residential property and housing opportunities designed for comfortable living.",
    },
    {
      icon: MapPinned,
      title: "Land & Plot Projects",
      description:
        "Property and land projects located in carefully selected development areas.",
    },
    {
      icon: Ruler,
      title: "Property Development",
      description:
        "Planning and development of property projects with a focus on practical value.",
    },
    {
      icon: FileCheck2,
      title: "Property Support",
      description:
        "Support throughout property-related documentation and project processes.",
    },
    {
      icon: Handshake,
      title: "Investment Opportunities",
      description:
        "Property opportunities for individuals looking for long-term investment value.",
    },
    {
      icon: ShieldCheck,
      title: "Customer Support",
      description:
        "Dedicated assistance to help customers make informed property decisions.",
    },
  ];

  const expertise = [
    "Property Planning",
    "Land Development",
    "Project Management",
    "Documentation Support",
    "Property Consultation",
    "Customer Assistance",
  ];

  const process = [
    "Requirement",
    "Property Selection",
    "Verification",
    "Planning",
    "Booking",
    "Registration",
    "Handover",
  ];

  return (
    <section
      style={{
        padding: "60px 20px",
        backgroundColor: "#ffffff",
        fontFamily: "sans-serif",
        marginTop: "-50px",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        {/* ================= HERO ================= */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            alignItems: "center",
            gap: "50px",
            marginBottom: "70px",
          }}
        >
          {/* Content */}
          <div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "15px",
                color: brandPurple,
                fontSize: "12px",
                fontWeight: "700",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              <Building2 size={16} />
              Iconic Greenland
            </div>

            <h2
              style={{
                margin: "0 0 18px",
                fontSize: "clamp(30px, 4vw, 42px)",
                lineHeight: "1.2",
                fontWeight: "800",
                color: "#1e293b",
              }}
            >
              Building Better
              <br />
              <span style={{ color: brandPurple }}>Property Opportunities</span>
            </h2>

            <div
              style={{
                width: "55px",
                height: "3px",
                backgroundColor: brandBlue,
                marginBottom: "22px",
              }}
            />

            <p
              style={{
                margin: "0 0 15px",
                color: "#64748b",
                fontSize: "15px",
                lineHeight: "1.8",
              }}
            >
              Iconic Greenland is a property-focused business concern involved
              in land, residential and real estate opportunities. Our focus is
              to create practical property solutions for customers and
              investors.
            </p>

            <p
              style={{
                margin: 0,
                color: "#64748b",
                fontSize: "15px",
                lineHeight: "1.8",
              }}
            >
              From property selection and planning to documentation and customer
              support, we aim to make the property journey simple, transparent
              and convenient.
            </p>
          </div>

          {/* Image */}
          <div>
            <div
              style={{
                padding: "10px",
                border: `1px solid ${brandPurple}25`,
                borderRadius: "16px",
                backgroundColor: "#ffffff",
              }}
            >
              <img
                src={bulding}
                alt="Iconic Greenland Property"
                style={{
                  display: "block",
                  width: "100%",
                  height: "360px",
                  objectFit: "cover",
                  borderRadius: "10px",
                }}
              />
            </div>
          </div>
        </div>

        {/* ================= WHAT WE DO ================= */}
        <div style={{ marginBottom: "65px" }}>
          <div
            style={{
              textAlign: "center",
              marginBottom: "35px",
            }}
          >
            <h3
              style={{
                margin: "0 0 10px",
                color: "#1e293b",
                fontSize: "28px",
                fontWeight: "800",
              }}
            >
              What We Do
            </h3>

            <p
              style={{
                margin: 0,
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              Property solutions focused on value, convenience and trust.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "20px",
            }}
          >
            {services.map((service, index) => {
              const Icon = service.icon;

              return (
                <div
                  key={service.title}
                  style={{
                    padding: "24px",
                    backgroundColor: "#ffffff",
                    border: `1px solid ${
                      index % 2 === 0 ? `${brandPurple}30` : `${brandBlue}30`
                    }`,
                    borderRadius: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "10px",
                      backgroundColor:
                        index % 2 === 0 ? `${brandPurple}10` : `${brandBlue}10`,
                      color: index % 2 === 0 ? brandPurple : brandBlue,
                      marginBottom: "16px",
                    }}
                  >
                    <Icon size={22} strokeWidth={1.8} />
                  </div>

                  <h4
                    style={{
                      margin: "0 0 8px",
                      color: "#1e293b",
                      fontSize: "17px",
                      fontWeight: "700",
                    }}
                  >
                    {service.title}
                  </h4>

                  <p
                    style={{
                      margin: 0,
                      color: "#64748b",
                      fontSize: "13px",
                      lineHeight: "1.7",
                    }}
                  >
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ================= EXPERTISE ================= */}
        <div style={{ marginBottom: "65px" }}>
          <div
            style={{
              textAlign: "center",
              marginBottom: "30px",
            }}
          >
            <h3
              style={{
                margin: "0 0 10px",
                color: "#1e293b",
                fontSize: "28px",
                fontWeight: "800",
              }}
            >
              Our Expertise
            </h3>

            <p
              style={{
                margin: 0,
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              Areas where Iconic Greenland focuses its efforts.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "12px",
            }}
          >
            {expertise.map((item, index) => (
              <div
                key={item}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  padding: "10px 17px",
                  borderRadius: "8px",
                  border: `1px solid ${
                    index % 2 === 0 ? `${brandPurple}45` : `${brandBlue}45`
                  }`,
                  color: index % 2 === 0 ? brandPurple : brandBlue,
                  backgroundColor: "#ffffff",
                  fontSize: "13px",
                  fontWeight: "600",
                }}
              >
                <span style={{ fontSize: "12px" }}>✓</span>
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* ================= DEVELOPMENT PROCESS ================= */}
        <div>
          <div
            style={{
              textAlign: "center",
              marginBottom: "30px",
            }}
          >
            <h3
              style={{
                margin: "0 0 10px",
                color: "#1e293b",
                fontSize: "28px",
                fontWeight: "800",
              }}
            >
              Our Property Process
            </h3>

            <p
              style={{
                margin: 0,
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              A simple process from property selection to handover.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "12px",
            }}
          >
            {process.map((step, index) => (
              <div
                key={step}
                style={{
                  padding: "16px 10px",
                  textAlign: "center",
                  borderRadius: "10px",
                  border: `1px solid ${
                    index % 2 === 0 ? `${brandPurple}30` : `${brandBlue}30`
                  }`,
                  backgroundColor: "#f8fafc",
                }}
              >
                <div
                  style={{
                    marginBottom: "7px",
                    color: index % 2 === 0 ? brandPurple : brandBlue,
                    fontSize: "12px",
                    fontWeight: "800",
                  }}
                >
                  {String(index + 1).padStart(2, "0")}
                </div>

                <div
                  style={{
                    color: "#475569",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  {step}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GreenlandAbout;
